import React, {useCallback, useEffect, useRef, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useHistory} from 'react-router-dom'
import {advanceHouseholdJoinWarning, getHouseholdJoinWarning, HouseholdJoinWarning} from '../../api'
import '../../style/household-join-warning.scss'

type HouseholdJoinWarningModalProps = {
  sessionId: string
  onCompleted: () => Promise<void>
  onJourneyLoaded?: (journey: HouseholdJoinWarning) => void
}

export default function HouseholdJoinWarningModal({sessionId, onCompleted, onJourneyLoaded}: HouseholdJoinWarningModalProps) {
  const history = useHistory()
  const [journey, setJourney] = useState<HouseholdJoinWarning | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [checkingStatus, setCheckingStatus] = useState(false)
  const statusRequest = useRef<symbol | null>(null)
  const submitting = useRef(false)
  const heading = useRef<HTMLHeadingElement>(null)

  useEffect(() => {
    let active = true
    statusRequest.current = null
    setCheckingStatus(false)
    setJourney(null)
    setError('')
    if (!sessionId) {
      setError('Open your household invitation and verify your mobile to continue.')
      return
    }
    getHouseholdJoinWarning(sessionId).then((result) => {
      if (active) {
        setJourney(result)
        onJourneyLoaded?.(result)
      }
    }).catch((reason) => {
      if (active) setError(reason instanceof Error ? reason.message : 'Unable to load the household warning.')
    })
    return () => { active = false; statusRequest.current = null }
  }, [sessionId, onJourneyLoaded])

  useEffect(() => { heading.current?.focus() }, [journey?.step])
  useEffect(() => {
    if (journey?.step !== 'submitted' || journey.processingState === 'manual_review') return
    let active = true
    const timer = window.setInterval(() => {
      getHouseholdJoinWarning(sessionId).then((result) => {
        if (active) { setJourney(result); setError('') }
      }).catch((reason) => {
        if (active) setError(reason instanceof Error ? reason.message : 'Unable to check transfer progress.')
      })
    }, 3000)
    return () => { active = false; window.clearInterval(timer) }
  }, [sessionId, journey?.step, journey?.processingState])
  const openProfile = useCallback(async () => {
    if (submitting.current) return
    submitting.current = true
    setBusy(true)
    setError('')
    try {
      await onCompleted()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to load your profile. Please try again.')
    } finally {
      submitting.current = false
      setBusy(false)
    }
  }, [onCompleted])

  useEffect(() => {
    if (journey?.step === 'completed') void openProfile()
    if (journey?.step === 'cancelled') history.replace('/application-landing')
  }, [journey?.step, openProfile, history])

  const act = async (action: 'continue' | 'back' | 'cancel' | 'confirm') => {
    if (submitting.current) return
    submitting.current = true
    setBusy(true)
    setError('')
    try {
      const result = await advanceHouseholdJoinWarning(sessionId, action)
      if (result.step === 'cancelled') history.replace('/application-landing')
      else setJourney(result)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Unable to continue. Please try again.')
    } finally {
      submitting.current = false
      setBusy(false)
    }
  }

  const checkStatus = async () => {
    if (statusRequest.current || submitting.current) return
    const request = Symbol('household-join-status')
    statusRequest.current = request
    setCheckingStatus(true)
    setError('')
    try {
      const result = await getHouseholdJoinWarning(sessionId)
      if (statusRequest.current === request) setJourney(result)
    } catch (reason) {
      if (statusRequest.current === request) {
        setError(reason instanceof Error ? reason.message : 'Unable to check transfer progress. Please try again.')
      }
    } finally {
      if (statusRequest.current === request) {
        statusRequest.current = null
        setCheckingStatus(false)
      }
    }
  }

  const firstScreen = journey?.step === 'warning-1'
  const submitted = journey?.step === 'submitted'
  const needsReview = submitted && journey.processingState === 'manual_review'
  const completed = journey?.step === 'completed'
  const confirmed = submitted || completed
  const canRetryProfile = completed && Boolean(error) && !busy
  const name = journey?.primaryRenterFirstName
  const count = journey?.activeSubmissionCount
  const applicationLabel = count === 1 ? 'application' : 'applications'

  return (
    <Modal
      show
      centered
      backdrop='static'
      keyboard={false}
      dialogClassName='household-join-warning-modal'
      aria-labelledby='household-warning-title'
      aria-busy={busy || checkingStatus || (confirmed && !error && !needsReview)}
      onEntered={() => heading.current?.focus()}
    >
      <Modal.Body className='household-join-warning-modal__body'>
        <div className='household-join-warning-modal__icon' aria-hidden='true'>
          <svg width='22' height='22' viewBox='0 0 20 20' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' focusable='false'>
            {firstScreen ? (
              <path d='M4 5.5h12M8 5.5V4a1.5 1.5 0 011.5-1.5h1A1.5 1.5 0 0112 4v1.5M5.5 5.5l.8 10A1.5 1.5 0 007.8 17h4.4a1.5 1.5 0 001.5-1.5l.8-10' />
            ) : <>
              <path d='M10 2.5l8 14H2l8-14Z' />
              <path d='M10 8v3.5' />
              <circle cx='10' cy='14' r='.5' fill='currentColor' stroke='none' />
            </>}
          </svg>
        </div>
        <h1 id='household-warning-title' className='household-join-warning-modal__title' ref={heading} tabIndex={-1}>
          {!journey ? 'Join household' : firstScreen ? `Join ${name}’s household?` : 'Are you sure?'}
        </h1>
        {!journey && !error && <p role='status'>Loading your household details…</p>}
        {journey && journey.step !== 'cancelled' && <>
          {firstScreen ? <>
            <div className='household-join-warning-modal__message success'>
              <span aria-hidden='true'>✓</span>
              <p>Your Doora Card comes with you. Your income, ID, documents, pets and vehicles all carry over.</p>
            </div>
            <div className='household-join-warning-modal__message danger'>
              <span aria-hidden='true'>×</span>
              <p>You will not have to enter anything again.</p>
            </div>
            <div className='household-join-warning-modal__message success'>
              <span aria-hidden='true'>✓</span>
              <p>You are currently part of a household with <strong>{count} sent {applicationLabel}</strong>. Joining {name}’s household withdraws you from {count === 1 ? 'this application' : `all ${count}`}.</p>
            </div>
          </> : <>
            <p>This is your last chance to go back. Joining {name}’s household will withdraw {count === 1 ? '' : 'all '}<strong>{count} sent {applicationLabel}</strong>. This cannot be undone.</p>
          </>}
          {submitted && <>
            <p role='status'>{needsReview
              ? 'Your transfer needs support review. This transfer has not changed your household or withdrawn the original applications. Please contact support.'
              : 'Joining household. Please wait…'}</p>
            {journey.lastProcessingError && journey.processingState !== 'manual_review' && <p>{journey.lastProcessingError}</p>}
          </>}
          <div className='household-join-warning-modal__actions'>
            <button type='button' className='btn btn-outline-secondary' disabled={busy || confirmed} onClick={() => act(firstScreen ? 'cancel' : 'back')}>
              {firstScreen ? 'Cancel' : 'Go Back'}
            </button>
            <button type='button' className={needsReview ? 'btn btn-outline-secondary' : 'btn btn-danger'} disabled={busy || checkingStatus || (confirmed && !canRetryProfile && !needsReview)} onClick={() => {
              if (needsReview) void checkStatus()
              else if (canRetryProfile) void openProfile()
              else if (!confirmed) void act(firstScreen ? 'continue' : 'confirm')
            }}>
              {needsReview ? checkingStatus ? 'Checking status…' : 'Check status' : canRetryProfile ? 'Retry opening profile' : confirmed ? 'Joining household…' : busy ? 'Please wait…' : firstScreen ? 'Continue' : 'Join Household'}
            </button>
          </div>
        </>}
        {error && <p className='household-join-warning-modal__error' role='alert'>{error}</p>}
        {!journey && error && <a href='/application-landing'>Return to Doora</a>}
      </Modal.Body>
    </Modal>
  )
}
